import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Crown,
  Eye,
  Home,
  Mail,
  Plus,
  Settings,
  Trash2,
  UserCheck,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { queryClient, trpc, trpcClient } from '@/utils/trpc';

interface InviteFormData {
  email: string;
  role: 'admin' | 'member' | 'viewer';
}

interface HouseholdFormData {
  name: string;
  currency: string;
}

export function HouseholdSettings() {
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const [inviteData, setInviteData] = useState<InviteFormData>({
    email: '',
    role: 'member',
  });

  const [householdData, setHouseholdData] = useState<HouseholdFormData>({
    name: '',
    currency: 'BRL',
  });

  // Queries
  const currentHousehold = useQuery(trpc.household.current.queryOptions());
  const allHouseholds = useQuery(trpc.household.list.queryOptions());

  // Mutations
  const createHousehold = useMutation({
    mutationFn: (data: HouseholdFormData) =>
      trpcClient.household.create.mutate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['household'] });
      setIsCreateDialogOpen(false);
      setHouseholdData({ name: '', currency: 'BRL' });
    },
    onError: (error: any) => {
      toast.error(`Erro ao criar casa: ${error.message}`);
    },
  });

  const updateHousehold = useMutation({
    mutationFn: (data: Partial<HouseholdFormData>) =>
      trpcClient.household.update.mutate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['household'] });
      setIsEditDialogOpen(false);
    },
    onError: (error: any) => {
      toast.error(`Erro ao atualizar casa: ${error.message}`);
    },
  });

  const inviteMember = useMutation({
    mutationFn: (data: InviteFormData) =>
      trpcClient.household.inviteMember.mutate(data),
    onSuccess: (result: any) => {
      queryClient.invalidateQueries({ queryKey: ['household', 'current'] });
      setIsInviteDialogOpen(false);
      setInviteData({ email: '', role: 'member' });

      if (result?.pendingInvite) {
        toast.success(
          'Convite enviado por email! A pessoa precisa se cadastrar para aceitar.'
        );
      } else {
        toast.success('Membro convidado com sucesso!');
      }
    },
    onError: (error: any) => {
      toast.error(`Erro ao convidar membro: ${error.message}`);
    },
  });

  const removeMember = useMutation({
    mutationFn: (userId: string) =>
      trpcClient.household.removeMember.mutate({ userId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['household', 'current'] });
      toast.success('Membro removido com sucesso!');
    },
    onError: (error: any) => {
      toast.error(`Erro ao remover membro: ${error.message}`);
    },
  });

  const updateMemberRole = useMutation({
    mutationFn: ({
      userId,
      role,
    }: {
      userId: string;
      role: 'admin' | 'member' | 'viewer';
    }) => trpcClient.household.updateMemberRole.mutate({ userId, role }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['household', 'current'] });
      toast.success('Papel do membro atualizado com sucesso!');
    },
    onError: (error: any) => {
      toast.error(`Erro ao atualizar papel: ${error.message}`);
    },
  });

  const handleInviteSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteData.email.trim()) {
      toast.error('Digite um email válido');
      return;
    }
    inviteMember.mutate(inviteData);
  };

  const handleCreateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!householdData.name.trim()) {
      toast.error('Digite um nome para a casa');
      return;
    }
    createHousehold.mutate(householdData);
  };

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!householdData.name.trim()) {
      toast.error('Digite um nome para a casa');
      return;
    }
    updateHousehold.mutate(householdData);
  };

  const handleRemoveMember = (userId: string, userName: string) => {
    toast(`Remover "${userName}" da casa?`, {
      action: {
        label: 'Remover',
        onClick: () => removeMember.mutate(userId),
      },
      cancel: {
        label: 'Cancelar',
        onClick: () => {},
      },
    });
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin':
        return <Crown className="h-4 w-4" />;
      case 'member':
        return <UserCheck className="h-4 w-4" />;
      case 'viewer':
        return <Eye className="h-4 w-4" />;
      default:
        return <UserCheck className="h-4 w-4" />;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'destructive';
      case 'member':
        return 'default';
      case 'viewer':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const household = currentHousehold.data;

  return (
    <div className="space-y-6">
      {/* Current Household */}
      {household ? (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Home className="h-5 w-5" />
                <div>
                  <CardTitle>{household.name}</CardTitle>
                  <CardDescription>
                    {household.members.length} membro(s) • Moeda:{' '}
                    {household.currency}
                  </CardDescription>
                </div>
              </div>
              <div className="flex gap-2">
                <Dialog
                  onOpenChange={setIsEditDialogOpen}
                  open={isEditDialogOpen}
                >
                  <DialogTrigger asChild>
                    <Button
                      disabled={household.role !== 'admin'}
                      onClick={() =>
                        setHouseholdData({
                          name: household.name,
                          currency: household.currency,
                        })
                      }
                      size="sm"
                      variant="outline"
                    >
                      <Settings className="h-4 w-4" />
                      Editar
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <form onSubmit={handleEditSubmit}>
                      <DialogHeader>
                        <DialogTitle>Editar Casa</DialogTitle>
                        <DialogDescription>
                          Atualize as informações da sua casa
                        </DialogDescription>
                      </DialogHeader>

                      <div className="grid gap-4 py-4">
                        <div className="grid gap-2">
                          <Label htmlFor="editName">Nome da Casa</Label>
                          <Input
                            id="editName"
                            onChange={(e) =>
                              setHouseholdData((prev) => ({
                                ...prev,
                                name: e.target.value,
                              }))
                            }
                            placeholder="Ex: Família Silva"
                            value={householdData.name}
                          />
                        </div>

                        <div className="grid gap-2">
                          <Label htmlFor="editCurrency">Moeda</Label>
                          <Select
                            onValueChange={(value) =>
                              setHouseholdData((prev) => ({
                                ...prev,
                                currency: value,
                              }))
                            }
                            value={householdData.currency}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="BRL">Real (BRL)</SelectItem>
                              <SelectItem value="USD">Dólar (USD)</SelectItem>
                              <SelectItem value="EUR">Euro (EUR)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <DialogFooter>
                        <Button
                          onClick={() => setIsEditDialogOpen(false)}
                          type="button"
                          variant="outline"
                        >
                          Cancelar
                        </Button>
                        <Button
                          disabled={updateHousehold.isPending}
                          type="submit"
                        >
                          {updateHousehold.isPending
                            ? 'Atualizando...'
                            : 'Atualizar'}
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>

                {household.role === 'admin' && (
                  <Dialog
                    onOpenChange={setIsInviteDialogOpen}
                    open={isInviteDialogOpen}
                  >
                    <DialogTrigger asChild>
                      <Button className="gap-2">
                        <Plus className="h-4 w-4" />
                        Convidar Membro
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <form onSubmit={handleInviteSubmit}>
                        <DialogHeader>
                          <DialogTitle>Convidar Membro</DialogTitle>
                          <DialogDescription>
                            Convide alguém para fazer parte da sua casa
                            financeira
                          </DialogDescription>
                        </DialogHeader>

                        <div className="grid gap-4 py-4">
                          <div className="grid gap-2">
                            <Label htmlFor="email">Email</Label>
                            <Input
                              id="email"
                              onChange={(e) =>
                                setInviteData((prev) => ({
                                  ...prev,
                                  email: e.target.value,
                                }))
                              }
                              placeholder="exemplo@email.com"
                              type="email"
                              value={inviteData.email}
                            />
                          </div>

                          <div className="grid gap-2">
                            <Label htmlFor="role">Papel</Label>
                            <Select
                              onValueChange={(
                                value: 'admin' | 'member' | 'viewer'
                              ) =>
                                setInviteData((prev) => ({
                                  ...prev,
                                  role: value,
                                }))
                              }
                              value={inviteData.role}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="admin">
                                  <div className="flex items-center gap-2">
                                    <Crown className="h-4 w-4" />
                                    Admin - Controle total
                                  </div>
                                </SelectItem>
                                <SelectItem value="member">
                                  <div className="flex items-center gap-2">
                                    <UserCheck className="h-4 w-4" />
                                    Membro - Pode editar e criar
                                  </div>
                                </SelectItem>
                                <SelectItem value="viewer">
                                  <div className="flex items-center gap-2">
                                    <Eye className="h-4 w-4" />
                                    Visualizador - Apenas visualizar
                                  </div>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <DialogFooter>
                          <Button
                            onClick={() => {
                              setIsInviteDialogOpen(false);
                              setInviteData({ email: '', role: 'member' });
                            }}
                            type="button"
                            variant="outline"
                          >
                            Cancelar
                          </Button>
                          <Button
                            disabled={inviteMember.isPending}
                            type="submit"
                          >
                            {inviteMember.isPending
                              ? 'Convidando...'
                              : 'Convidar'}
                          </Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Members List */}
            <div className="space-y-3">
              <h4 className="flex items-center gap-2 font-medium">
                <Users className="h-4 w-4" />
                Membros da Casa
              </h4>

              {household.members.map((member) => (
                <div
                  className="flex items-center justify-between rounded-lg border p-3"
                  key={member.user.id}
                >
                  <div className="flex items-center gap-3">
                    {member.user.image && (
                      <img
                        alt={member.user.name || ''}
                        className="h-8 w-8 rounded-full"
                        src={member.user.image}
                      />
                    )}
                    <div>
                      <p className="font-medium">{member.user.name}</p>
                      <p className="flex items-center gap-1 text-muted-foreground text-sm">
                        <Mail className="h-3 w-3" />
                        {member.user.email}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Badge
                      className="gap-1"
                      variant={getRoleColor(member.role) as any}
                    >
                      {getRoleIcon(member.role)}
                      {member.role === 'admin'
                        ? 'Admin'
                        : member.role === 'member'
                          ? 'Membro'
                          : 'Visualizador'}
                    </Badge>

                    {household.role === 'admin' &&
                      household.members.length > 1 && (
                        <Button
                          className="h-8 w-8 p-0 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                          onClick={() =>
                            handleRemoveMember(
                              member.user.id,
                              member.user.name || ''
                            )
                          }
                          size="sm"
                          variant="ghost"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Nenhuma Casa Encontrada</CardTitle>
            <CardDescription>
              Crie uma casa para começar a gerenciar suas finanças em família
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Dialog
              onOpenChange={setIsCreateDialogOpen}
              open={isCreateDialogOpen}
            >
              <DialogTrigger asChild>
                <Button className="gap-2">
                  <Plus className="h-4 w-4" />
                  Criar Nova Casa
                </Button>
              </DialogTrigger>
              <DialogContent>
                <form onSubmit={handleCreateSubmit}>
                  <DialogHeader>
                    <DialogTitle>Criar Nova Casa</DialogTitle>
                    <DialogDescription>
                      Configure uma casa para gerenciar finanças em família
                    </DialogDescription>
                  </DialogHeader>

                  <div className="grid gap-4 py-4">
                    <div className="grid gap-2">
                      <Label htmlFor="name">Nome da Casa</Label>
                      <Input
                        id="name"
                        onChange={(e) =>
                          setHouseholdData((prev) => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                        placeholder="Ex: Família Silva"
                        value={householdData.name}
                      />
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor="currency">Moeda</Label>
                      <Select
                        onValueChange={(value) =>
                          setHouseholdData((prev) => ({
                            ...prev,
                            currency: value,
                          }))
                        }
                        value={householdData.currency}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="BRL">Real (BRL)</SelectItem>
                          <SelectItem value="USD">Dólar (USD)</SelectItem>
                          <SelectItem value="EUR">Euro (EUR)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <DialogFooter>
                    <Button
                      onClick={() => {
                        setIsCreateDialogOpen(false);
                        setHouseholdData({ name: '', currency: 'BRL' });
                      }}
                      type="button"
                      variant="outline"
                    >
                      Cancelar
                    </Button>
                    <Button disabled={createHousehold.isPending} type="submit">
                      {createHousehold.isPending ? 'Criando...' : 'Criar Casa'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
